import { useEffect, useState, useCallback } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Link2, Unlink } from "lucide-react";

export default function ReconciliationTab({ accounts }) {
  const [accountId, setAccountId] = useState("");
  const [statements, setStatements] = useState([]);
  const [current, setCurrent] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [movements, setMovements] = useState([]);
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatements = useCallback(() => {
    if (!accountId) { setStatements([]); return; }
    api.get("/bank-statements", { params: { account_id: accountId } }).then(({ data }) => setStatements(data));
  }, [accountId]);
  useEffect(loadStatements, [loadStatements]);

  const openStatement = async (id) => {
    const [{ data: st }, { data: sugg }] = await Promise.all([
      api.get(`/bank-statements/${id}`), api.get(`/bank-statements/${id}/suggestions`),
    ]);
    const { data: movs } = await api.get("/fin-movements", { params: { account_kind: "banco", account_id: st.account_id, status: "activo" } });
    setCurrent(st); setSuggestions(sugg); setMovements(movs.filter((m) => !m.reconciled && m.type !== "reverso"));
  };

  const importFile = async (file) => {
    if (!accountId) return toast.error("Selecciona la cuenta");
    if (!period.trim()) return toast.error("Indica el periodo (ej. Junio 2026)");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/bank-statements/import?account_id=${accountId}&period_label=${encodeURIComponent(period)}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importadas ${data.rows.length} filas`);
      loadStatements(); openStatement(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al importar"); }
    finally { setBusy(false); }
  };

  const match = async (rowId, movementId) => {
    try {
      await api.post(`/bank-statements/${current.id}/match`, { row_id: rowId, movement_id: movementId });
      toast.success(movementId ? "Conciliado" : "Desvinculado");
      openStatement(current.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const matched = current ? current.rows.filter((r) => r.matched_movement_id) : [];
  const unmatched = current ? current.rows.filter((r) => !r.matched_movement_id) : [];
  const diff = unmatched.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="space-y-3 text-xs" data-testid="reconciliation-tab">
      <div className="panel p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">// Importar estado de cuenta (CSV o Excel: fecha, descripción, referencia, monto o cargo/abono)</div>
        <div className="flex gap-2 flex-wrap items-center">
          <select className="input-cyber px-2 py-1.5" value={accountId} onChange={(e) => { setAccountId(e.target.value); setCurrent(null); }} data-testid="recon-account">
            <option value="">Cuenta bancaria…</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <input className="input-cyber px-2 py-1.5" placeholder="Periodo (ej. Junio 2026)" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="recon-period" />
          <label className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest cursor-pointer">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : <Upload className="w-3.5 h-3.5 inline mr-1" />} Importar
            <input type="file" className="hidden" accept=".csv,.xlsx,.xls" data-testid="recon-file"
              onChange={(e) => { if (e.target.files[0]) importFile(e.target.files[0]); e.target.value = ""; }} />
          </label>
        </div>
        {statements.length > 0 && (
          <div className="flex gap-1 flex-wrap pt-1">
            {statements.map((s) => (
              <button key={s.id} onClick={() => openStatement(s.id)} data-testid={`recon-st-${s.id}`}
                className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest"
                style={{ border: `1px solid ${current?.id === s.id ? "var(--cyan)" : "var(--border)"}`, color: current?.id === s.id ? "var(--cyan)" : "var(--text-dim)" }}>
                {s.period_label}
              </button>
            ))}
          </div>
        )}
      </div>

      {current && (
        <>
          <div className="flex gap-3 flex-wrap font-mono text-[11px]">
            <span style={{ color: "#86efac" }}>✓ conciliadas: {matched.length}</span>
            <span style={{ color: "#f59e0b" }}>pendientes: {unmatched.length}</span>
            <span className="text-slate-400">diferencia sin conciliar: <b style={{ color: Math.abs(diff) < 0.01 ? "#86efac" : "#fca5a5" }}>{fmtMXN(diff)}</b></span>
          </div>
          {current.rows.map((row) => {
            const sugg = suggestions[row.id] || [];
            return (
              <div key={row.id} className="panel px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`recon-row-${row.id}`}
                style={{ borderLeft: `3px solid ${row.matched_movement_id ? "#16a34a" : "#f59e0b"}` }}>
                <div className="flex-1 min-w-0 text-slate-300 truncate">
                  {fmtDate(row.date)} · {row.description || "—"}{row.reference ? ` · ref ${row.reference}` : ""}
                </div>
                <span className="font-mono font-bold" style={{ color: row.amount >= 0 ? "#86efac" : "#fca5a5" }}>{fmtMXN(row.amount)}</span>
                {row.matched_movement_id ? (
                  <button onClick={() => match(row.id, null)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" data-testid={`recon-unmatch-${row.id}`} style={{ color: "#f59e0b", border: "1px solid #f59e0b" }}>
                    <Unlink className="w-3 h-3 inline" /> Desvincular
                  </button>
                ) : sugg.length > 0 ? (
                  <div className="flex gap-1 flex-wrap">
                    {sugg.map((m) => (
                      <button key={m.id} onClick={() => match(row.id, m.id)} className="btn-ghost px-2 py-1 text-[10px]" data-testid={`recon-match-${row.id}-${m.id}`}
                        style={{ color: "#86efac", border: "1px solid #16a34a" }}>
                        <Link2 className="w-3 h-3 inline" /> {m.folio} {fmtDate(m.date)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <select className="input-cyber px-2 py-1 text-[10px]" defaultValue="" data-testid={`recon-manual-${row.id}`}
                    onChange={(e) => { if (e.target.value) match(row.id, e.target.value); }}>
                    <option value="">Vincular manual…</option>
                    {movements.map((m) => (
                      <option key={m.id} value={m.id}>{m.folio} · {fmtDate(m.date)} · {m.direction === "in" ? "+" : "−"}{fmtMXN(m.amount)}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
